import {
  ActionIcon,
  Box,
  Group,
  Menu,
  NumberInput,
  ScrollArea,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { IconChevronDown, IconSearch, IconTrash, IconX } from '@tabler/icons-react';
import React, { Fragment, useState } from 'react';
import { ClientListItem } from 'src/admin/users/types';
import { ownerCandidates } from 'src/financial/financial-data/financial-item/ownerCandidates';
import {
  formatOwnerShare,
  hasUnrecordedCoOwner,
  ownerShares,
  type OwnerShare,
  roundShare,
  shareOfValue,
  UNRECORDED_CO_OWNER,
} from 'src/financial/financial-data/ownerShares';
import { ViewField } from 'src/non-financial-data/components/fields/ViewFormField';
import type { FamilyMember } from 'src/non-financial-data/v2/family/useFamilyMembers';
import { Button } from 'src/design-system/Button';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { formatName, NameFormat } from 'src/utils/nameFormatting';

export type OwnerRowValue = {
  id?: string | null;
  client_id?: string | null;
  external_name?: string | null;
  ownership_percentage?: number | null;
};

const EXTERNAL = 'external';

// Products whose backend persists an owners list. The owner editor must only run for
// these; other products (crypto, collectibles, pensions) have no owner tables, so
// collecting owners for them would silently drop them on save.
const OWNER_SUPPORTED_PRODUCTS = new Set([
  'Property',
  'InvestmentAndCash',
  'Mortgage',
  'Loan',
  'SpendingAccount',
  'Protection',
]);

export const productSupportsOwners = (product: string): boolean =>
  OWNER_SUPPORTED_PRODUCTS.has(product);

/** A share is captured to the nearest hundredth of a percent. */
const SHARE_DECIMAL_PLACES = 2;

/** The owners' shares, to the same hundredth of a percent each one is captured at. */
function ownersShareSum(owners: OwnerRowValue[]): number {
  return roundShare(
    owners.reduce((acc, owner) => acc + (Number(owner.ownership_percentage) || 0), 0),
  );
}

// Some splits cannot be written exactly in hundredths: three equal owners come to 33.33% each, a
// hundredth short of the whole, and six come to 16.67% each, two hundredths over it. Each share
// carries its own rounding, so a hundredth is forgiven per owner rather than making the adviser
// pick one owner to carry the remainder. That stays far too small to let a real mistake through:
// 33% each is a point out. Compared in hundredths, since 99.99 and 100 are a shade further apart
// in binary than the hundredth they differ by. `check_ownership_allocations` applies the same
// allowance, and the two have to agree: a split the form accepts but the backend refuses comes
// back as an error with nothing to act on.
export function ownersSumTo100(owners: OwnerRowValue[]): boolean {
  return Math.abs(Math.round(ownersShareSum(owners) * 100) - 10000) <= owners.length;
}

/**
 * A share describes how an item is split, so a split is only checked once two owners are recorded
 * to split it between. A sole item is wholly its owner's whatever the field says. A joint item's
 * lone holder may type a share, which leaves the rest to the co-owner nobody has recorded yet, so
 * there is no other share to check it against. Demanding 100% of a lone owner pushed the adviser
 * to type it — which saved a joint item recording one owner as a sole one.
 */
const ownersNeedShares = (owners: OwnerRowValue[]) => owners.length >= 2;

const everyShareEntered = (owners: OwnerRowValue[]) =>
  owners.every(owner => owner.ownership_percentage != null);

const someShareEntered = (owners: OwnerRowValue[]) =>
  owners.some(owner => owner.ownership_percentage != null);

/**
 * Owners named with no shares between them split the item evenly, written down at the hundredth
 * a share is captured to. A split with any share already given is the adviser's or the
 * document's, and is left as it is.
 */
export function withEvenSharesWhereBlank<Owner extends OwnerRowValue>(
  owners: Owner[] | null | undefined,
): Owner[] | null | undefined {
  if (owners == null || !ownersNeedShares(owners) || someShareEntered(owners)) return owners;
  const shares = ownerShares(
    owners.map(owner => owner.ownership_percentage),
    true,
  );
  return owners.map((owner, index) => ({
    ...owner,
    ownership_percentage: roundShare(shares[index].percentage),
  }));
}

/**
 * The message under the rows, or none. A split only half entered is not yet wrong — the adviser
 * is still typing it — so the total is checked only once every owner carries a share.
 */
export function shareTotalError(owners: OwnerRowValue[]): string | null {
  if (!ownersNeedShares(owners) || !everyShareEntered(owners)) return null;
  if (ownersSumTo100(owners)) return null;
  return `Shares must add up to 100% (currently ${ownersShareSum(owners)}%).`;
}

/**
 * Why a split cannot be saved, or none. Stricter than the message under the rows, because a
 * half-entered split is only harmless while it is still being typed: saved, the shares that were
 * entered are thrown away. ``owner_share`` trusts the percentages only when every owner carries
 * one, so 70% against one owner and a blank against the other comes back as an even 50/50 — the
 * adviser's 70% silently gone. Either every owner has a share or none does.
 */
export function shareSaveError(owners: OwnerRowValue[]): string | null {
  if (!ownersNeedShares(owners)) return null;
  if (someShareEntered(owners) && !everyShareEntered(owners)) {
    return 'Enter a share for every owner, or leave them all blank for an even split.';
  }
  return shareTotalError(owners);
}

export const BLANK_OWNER_NAME_ERROR = 'Enter this owner’s name';

/** A blank share field offers the share the Financial tab assumes for it, until one is typed. */
const sharePlaceholder = (share: OwnerShare, isValuation: boolean) => {
  if (share.isAssumed) return `${roundShare(share.percentage)}%`;
  return isValuation ? '0%' : undefined;
};

const NO_SHARE_YET: OwnerShare = { percentage: 0, isAssumed: false };

/**
 * External owners saved with no name, by row. The backend blanks '' to None and rejects the save
 * with nothing to say which row was at fault, so the form asks for the name against the field.
 */
export function blankExternalNameIndexes(owners: OwnerRowValue[]): number[] {
  return owners.flatMap((owner, index) =>
    !owner.client_id && !(owner.external_name ?? '').trim() ? [index] : [],
  );
}

export const isExternalOwner = (owner: OwnerRowValue): boolean => !owner.client_id;

/** 'valuation' is the reworked financial form's table; 'classic' is the original inline list. */
type Variant = 'classic' | 'valuation';

const CLASSIC_NAME_WIDTH = 200;
const VALUATION_NAME_WIDTH = '50%';
const PERCENTAGE_WIDTH = 90;
const AMOUNT_WIDTH = 140;
const CLASSIC_AMOUNT_WIDTH = 110;
const REMOVE_WIDTH = 32;

/** A figure the adviser reads off rather than types into, in the weight read mode uses. */
function ReadOnlyCell({
  width,
  muted = false,
  children,
}: {
  width: number;
  muted?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Text size="sm" w={width} ta="right" c="gray.10" fw={muted ? undefined : FONT_WEIGHTS.semibold}>
      {children}
    </Text>
  );
}

type Props = {
  owners: OwnerRowValue[];
  onChange: (next: OwnerRowValue[]) => void;
  members: FamilyMember[];
  /** Names a client owner outside the family. */
  resolveOwnerName?: (clientId: string) => string | undefined;
  /**
   * The clients the viewer can add as owners besides the family members, offered under "Add from
   * client's list". Without it the menu offers family members and external owners only.
   */
  otherClients?: ClientListItem[];
  /**
   * The item's total value. Each owner's amount is read off it through their share and never
   * typed: only the share is recorded, so the total and the shares are the only figures to edit.
   * Without one the owners show no amount, since there is nothing to take a share of.
   */
  total?: number | null;
  /**
   * Renders an owner's slice of `total`. Supplied by the caller so each surface keeps its own
   * money convention: the create form uses the tenant's currency, the Data Upload Review card
   * uses the currency the detection itself carries, matching the value shown above it.
   */
  formatAmount: (amount: number) => string;
  /** Shows a joint toggle that reveals the rows; without it the rows always show. */
  showJointToggle?: boolean;
  isJoint?: boolean;
  onJointChange?: (joint: boolean) => void;
  /** Locks the rows once the surface has saved them and further edits would go nowhere. */
  disabled?: boolean;
  /**
   * A surface that is being read rather than edited. Unlike `disabled`, which leaves the
   * controls on screen greyed out, this prints the shares as text — Mantine renders a disabled
   * input at 0.6 opacity in its muted colour, which is barely legible, and the fields around
   * this table read as plain text in the same mode.
   */
  readOnly?: boolean;
  /** A client whose row cannot be removed: the client a new item is created through. */
  lockedClientId?: string;
  variant?: Variant;
  /** Per-row name errors raised by a blocked save, keyed by row index. */
  nameErrors?: Record<number, string>;
};

// Controlled owners editor shared by the create form (via OwnerSharesSection) and the
// Data Upload Review card. Owns no form/route state — it renders the given owners and
// reports edits through onChange.
export function OwnersEditor({
  owners,
  onChange,
  members,
  resolveOwnerName,
  otherClients,
  total,
  formatAmount,
  showJointToggle = false,
  isJoint = false,
  onJointChange,
  disabled = false,
  readOnly = false,
  lockedClientId,
  variant = 'classic',
  nameErrors,
}: Props) {
  const usedClientIds = new Set(
    owners.flatMap(owner => (owner.client_id ? [owner.client_id] : [])),
  );
  const availableMembers = members.filter(member => !usedClientIds.has(member.clientId));
  const isValuation = variant === 'valuation';
  const nameWidth = isValuation ? VALUATION_NAME_WIDTH : CLASSIC_NAME_WIDTH;

  const memberName = (clientId: string) => {
    const member = members.find(candidate => candidate.clientId === clientId);
    if (member)
      return formatName(member.client.firstName, member.client.lastName, NameFormat.NATURAL);
    return resolveOwnerName?.(clientId) ?? 'Unknown owner';
  };

  const addOwner = (value: string) => {
    const added =
      value === EXTERNAL
        ? { external_name: '', ownership_percentage: null }
        : { client_id: value, ownership_percentage: null };
    const next = [...owners, added];
    if (!showsUnrecordedCoOwner) return onChange(next);
    // The co-owner the item was missing has arrived, so the split the rows were already showing
    // is written down rather than left for the adviser to retype.
    return onChange(
      next.map((owner, index) =>
        index === 0
          ? { ...owner, ownership_percentage: roundShare(holderShare.percentage) }
          : { ...owner, ownership_percentage: unrecordedShare.percentage },
      ),
    );
  };

  const removeOwner = (index: number) => onChange(owners.filter((_, i) => i !== index));

  // Every item belongs to a client, so the last household owner cannot be taken off it: the rows
  // the form sends replace the item's owners wholesale, and a list naming only external owners
  // would leave the item with no owner inside the household at all.
  const clientOwnerCount = owners.filter(owner => owner.client_id).length;
  const isLastClientOwner = (owner: OwnerRowValue) => !!owner.client_id && clientOwnerCount === 1;

  const updateOwner = (index: number, patch: Partial<OwnerRowValue>) =>
    onChange(owners.map((owner, i) => (i === index ? { ...owner, ...patch } : owner)));

  // Making an item with several owners sole means choosing which owner keeps it, and the switch
  // could only choose arbitrarily. So it stays joint until the adviser removes the other owners.
  const jointLocked = owners.length > 1;
  const jointChecked = isJoint || jointLocked;
  const showRows = showJointToggle ? jointChecked : true;

  // A joint item that records only its holder is a state the adviser is allowed to be in: they
  // know who holds it and not yet who with. The missing half is shown as a row of its own rather
  // than leaving a joint item looking singly owned — which is what pushed advisers to give the
  // holder 100% and save it as sole. Only a surface with the joint switch knows an item is joint;
  // without it the rows given are all there is, and several of them make it joint.
  const readsAsJoint = showJointToggle ? jointChecked : jointLocked;
  const showsUnrecordedCoOwner =
    showJointToggle && owners.length > 0 && hasUnrecordedCoOwner(isJoint, owners.length);

  // The rows of a joint item read the split the way the Financial tab does, so blank shares show
  // the even split it assumes rather than 0%. Read, they show exactly the shares the tab counts:
  // only the item form refuses a split that is partly entered or off 100%, so one saved through
  // Data Upload or the API reads as the tab reads it, not as typed. While editing, a split only
  // partly typed is still being entered: the shares typed so far show as typed and the blanks as
  // nothing yet.
  const shares = ownerShares(
    owners.map(owner => owner.ownership_percentage),
    readsAsJoint,
  );
  const isPartlyEntered = someShareEntered(owners) && !everyShareEntered(owners);
  const shownShare = (owner: OwnerRowValue, index: number): OwnerShare => {
    if (readOnly) return shares[index];
    if (owner.ownership_percentage != null) {
      return { percentage: Number(owner.ownership_percentage), isAssumed: false };
    }
    return readsAsJoint && !isPartlyEntered ? shares[index] : NO_SHARE_YET;
  };
  const holderShare = shares[0] ?? NO_SHARE_YET;
  const unrecordedShare: OwnerShare = {
    percentage: roundShare(100 - holderShare.percentage),
    isAssumed: holderShare.isAssumed,
  };

  // Read, the rows show the shares the tab counts, which always add up to 100%, so a saved total
  // off 100% has no row left to point at.
  const totalError = readOnly ? null : shareTotalError(owners);
  // A share read as a weight seldom gives a whole number of pounds, so a read slice is shown in
  // whole pounds beside the total, which is stated to the penny.
  const amountOf = (percentage: number) => {
    if (total == null) return undefined;
    const amount = shareOfValue(total, percentage);
    return formatAmount(readOnly ? Math.round(amount) : amount);
  };
  const addLabel = showsUnrecordedCoOwner ? 'Add co-owner' : 'Add owner';

  const sumWarning = totalError && (
    <Text size="xs" c="error.6">
      {totalError}
    </Text>
  );

  const externalMarker = (
    <Text size="sm" c="gray.7" style={{ whiteSpace: 'nowrap' }}>
      (External)
    </Text>
  );

  const ownerName = (owner: OwnerRowValue, index: number) => {
    if (owner.client_id) {
      return (
        <Text size="sm" fw={readOnly ? FONT_WEIGHTS.semibold : FONT_WEIGHTS.medium} c="gray.10">
          {memberName(owner.client_id)}
        </Text>
      );
    }

    // Being read, an owner from outside the household is named and marked in the same breath,
    // so the marker reads as part of who they are rather than as a note on an empty field.
    if (readOnly) {
      return (
        <Group gap={4} wrap="nowrap">
          <Text size="sm" fw={FONT_WEIGHTS.semibold} c="gray.10">
            {owner.external_name || 'Unnamed owner'}
          </Text>
          {externalMarker}
        </Group>
      );
    }

    const nameInput = (
      <TextInput
        placeholder="Owner name"
        variant={isValuation ? 'unstyled' : 'default'}
        disabled={disabled}
        error={nameErrors?.[index]}
        value={owner.external_name ?? ''}
        onChange={event => updateOwner(index, { external_name: event.currentTarget.value })}
      />
    );

    // Only the reworked table says in words that an owner sits outside the household; the
    // classic list leaves the input to fill its column as it always has. The input is allowed
    // to shrink so the marker keeps its place in the column rather than being pushed out of it.
    return isValuation ? (
      <Group gap={4} wrap="nowrap">
        <Box style={{ flex: 1, minWidth: 0 }}>{nameInput}</Box>
        {externalMarker}
      </Group>
    ) : (
      nameInput
    );
  };

  // An external owner's slice is recorded but sits outside the household's valuation, so it
  // reads in grey rather than the brand colour the household's own shares use.
  const shareBar = (owner: OwnerRowValue, percentage: number) => {
    const isExternal = isValuation && isExternalOwner(owner);
    const brandTrack = isValuation ? 'brand-1' : 'gray-1';
    const track = isExternal ? 'gray-2' : brandTrack;
    const fill = isExternal ? 'gray-4' : 'brand-3';
    return (
      <Box
        style={{
          flex: 1,
          height: 8,
          borderRadius: 4,
          background: `var(--mantine-color-${track})`,
        }}
      >
        <Box
          style={{
            height: 8,
            borderRadius: 4,
            width: `${Math.min(100, percentage)}%`,
            background: `var(--mantine-color-${fill})`,
          }}
        />
      </Box>
    );
  };

  // Rows the joint toggle keeps hidden are not worth building on every render.
  const rows =
    showRows &&
    owners.map((owner, index) => {
      const share = shownShare(owner, index);
      const { percentage } = share;
      return (
        <Group
          key={owner.id ?? index}
          align="center"
          wrap="nowrap"
          gap="md"
          px={isValuation ? 'md' : undefined}
          py={isValuation ? 'sm' : undefined}
          style={
            isValuation && index > 0
              ? { borderTop: '1px solid var(--mantine-color-gray-2)' }
              : undefined
          }
        >
          <Box style={{ width: nameWidth, ...(isValuation && { flexShrink: 0 }) }}>
            {ownerName(owner, index)}
          </Box>
          {shareBar(owner, percentage)}
          {readOnly ? (
            <ReadOnlyCell width={PERCENTAGE_WIDTH}>{formatOwnerShare(share)}</ReadOnlyCell>
          ) : (
            <NumberInput
              w={PERCENTAGE_WIDTH}
              min={0}
              max={100}
              decimalScale={SHARE_DECIMAL_PLACES}
              hideControls={isValuation}
              disabled={disabled}
              suffix="%"
              placeholder={sharePlaceholder(share, isValuation)}
              aria-label="Ownership share"
              value={owner.ownership_percentage ?? ''}
              onChange={value =>
                updateOwner(index, { ownership_percentage: value === '' ? null : Number(value) })
              }
            />
          )}
          <ReadOnlyCell width={isValuation ? AMOUNT_WIDTH : CLASSIC_AMOUNT_WIDTH} muted={!readOnly}>
            {amountOf(percentage)}
          </ReadOnlyCell>
          {readOnly && isValuation && <Box style={{ width: REMOVE_WIDTH }} />}
          {!readOnly && (
            <ActionIcon
              variant="subtle"
              color="gray"
              disabled={
                disabled ||
                isLastClientOwner(owner) ||
                (!!lockedClientId && owner.client_id === lockedClientId)
              }
              onClick={() => removeOwner(index)}
              aria-label="Remove owner"
            >
              {isValuation ? <IconX size={18} /> : <IconTrash size={16} />}
            </ActionIcon>
          )}
        </Group>
      );
    });

  const unrecordedCoOwnerRow = showRows && showsUnrecordedCoOwner && (
    <UnrecordedCoOwnerRow
      share={unrecordedShare}
      amount={amountOf(unrecordedShare.percentage)}
      nameWidth={nameWidth}
      isValuation={isValuation}
    />
  );

  return (
    <Stack gap="sm">
      <Group justify={showJointToggle ? 'space-between' : 'flex-end'} align="center">
        {showJointToggle &&
          (readOnly ? (
            <ViewField fieldLabel="Joint asset" value={jointChecked ? 'Yes' : 'No'} />
          ) : (
            <Switch
              checked={jointChecked}
              disabled={disabled || jointLocked}
              onChange={event => onJointChange?.(event.currentTarget.checked)}
              label="Joint asset. Enter each owner's share."
              description={
                jointLocked ? 'Remove the other owners to make this solely owned.' : undefined
              }
            />
          ))}
        {showRows && !readOnly && (
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <Button
                variant={isValuation ? 'text' : 'tertiary'}
                size="xs"
                px={isValuation ? undefined : 9}
                aria-label={addLabel}
                disabled={disabled}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                  {addLabel}
                  <IconChevronDown size={16} color="var(--mantine-color-brand-7)" />
                </span>
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              {availableMembers.length > 0 && (
                <Fragment>
                  <Menu.Label>Family members</Menu.Label>
                  {availableMembers.map(member => (
                    <Menu.Item key={member.clientId} onClick={() => addOwner(member.clientId)}>
                      {formatName(
                        member.client.firstName,
                        member.client.lastName,
                        NameFormat.NATURAL,
                      )}
                      {member.roleLabel ? ` (${member.roleLabel})` : ''}
                    </Menu.Item>
                  ))}
                </Fragment>
              )}
              <Menu.Item onClick={() => addOwner(EXTERNAL)}>Add external owner</Menu.Item>
              {otherClients && (
                <ClientListSubmenu
                  clients={otherClients}
                  excludedClientIds={
                    new Set([...members.map(member => member.clientId), ...usedClientIds])
                  }
                  onSelect={addOwner}
                />
              )}
            </Menu.Dropdown>
          </Menu>
        )}
      </Group>

      {showRows &&
        (isValuation ? (
          <>
            {owners.length > 0 && (
              <Box style={{ border: '1px solid var(--mantine-color-gray-2)', borderRadius: 16 }}>
                <Group align="center" wrap="nowrap" gap="md" px="md" pt="sm">
                  <Box style={{ width: nameWidth, flexShrink: 0 }}>
                    <Text size="xs" c="gray.7">
                      Owner
                    </Text>
                  </Box>
                  <Box style={{ flex: 1 }} />
                  <Text size="xs" c="gray.7" w={PERCENTAGE_WIDTH + AMOUNT_WIDTH + 16}>
                    Valuation
                  </Text>
                  <Box style={{ width: REMOVE_WIDTH }} />
                </Group>
                {rows}
                {unrecordedCoOwnerRow}
              </Box>
            )}
            {sumWarning}
          </>
        ) : (
          <Stack gap="xs">
            {rows}
            {unrecordedCoOwnerRow}
            {sumWarning}
          </Stack>
        ))}
    </Stack>
  );
}

type ClientListSubmenuProps = {
  clients: ClientListItem[];
  /** The family members, offered above, and the clients who already own the item. */
  excludedClientIds: Set<string>;
  onSelect: (clientId: string) => void;
};

/** Owners need not share a family, so any client the adviser can open can be added. */
function ClientListSubmenu({ clients, excludedClientIds, onSelect }: ClientListSubmenuProps) {
  const [search, setSearch] = useState('');
  const candidates = ownerCandidates(clients, excludedClientIds, search);

  return (
    <Menu.Sub onChange={opened => !opened && setSearch('')}>
      <Menu.Sub.Target>
        <Menu.Sub.Item>{"Add from client's list"}</Menu.Sub.Item>
      </Menu.Sub.Target>
      <Menu.Sub.Dropdown>
        <Box p="xxs" w={260}>
          <TextInput
            placeholder="Search clients"
            aria-label="Search clients"
            value={search}
            onChange={event => setSearch(event.currentTarget.value)}
            leftSection={<IconSearch size={16} />}
            data-autofocus
          />
        </Box>
        <ScrollArea.Autosize mah={240} type="auto">
          {candidates.length === 0 ? (
            <Text size="sm" c="gray.7" px="sm" py="xs">
              No matching clients
            </Text>
          ) : (
            candidates.map(client => (
              <Menu.Item key={client.id} onClick={() => onSelect(client.id)}>
                {formatName(client.firstName, client.lastName, NameFormat.NATURAL)}
              </Menu.Item>
            ))
          )}
        </ScrollArea.Autosize>
      </Menu.Sub.Dropdown>
    </Menu.Sub>
  );
}

/**
 * The rest of a joint item, held by a co-owner the adviser has not recorded. It carries no input:
 * there is nobody to attribute it to yet, and its share is whatever the holder's typed share
 * leaves, or the even split assumed when none is typed. Naming the co-owner through "Add
 * co-owner" replaces it with a row of their own.
 *
 * It takes the surrounding table's column widths so it lines up under the holder in both the
 * reworked valuation table and the classic list.
 */
function UnrecordedCoOwnerRow({
  share,
  amount,
  nameWidth,
  isValuation,
}: {
  share: OwnerShare;
  amount: string | undefined;
  nameWidth: number | string;
  isValuation: boolean;
}) {
  return (
    <Group
      align="center"
      wrap="nowrap"
      gap="md"
      data-testid="unrecorded-co-owner"
      px={isValuation ? 'md' : undefined}
      py={isValuation ? 'sm' : undefined}
      style={isValuation ? { borderTop: '1px solid var(--mantine-color-gray-2)' } : undefined}
    >
      <Box style={{ width: nameWidth, ...(isValuation && { flexShrink: 0 }) }}>
        <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.5" fs="italic">
          {UNRECORDED_CO_OWNER}
        </Text>
      </Box>
      <Box
        style={{ flex: 1, height: 8, borderRadius: 4, background: 'var(--mantine-color-gray-1)' }}
      >
        <Box
          style={{
            height: 8,
            borderRadius: 4,
            width: `${Math.min(100, share.percentage)}%`,
            background: 'var(--mantine-color-gray-3)',
          }}
        />
      </Box>
      <Text size="sm" w={PERCENTAGE_WIDTH} c="gray.5">
        {formatOwnerShare(share)}
      </Text>
      <Text size="sm" w={isValuation ? AMOUNT_WIDTH : CLASSIC_AMOUNT_WIDTH} ta="right" c="gray.5">
        {amount}
      </Text>
      {/* Keeps the row's columns under the ones above, where the remove button sits. */}
      <Box w={isValuation ? REMOVE_WIDTH : 28} />
    </Group>
  );
}
